// packages/report-components/src/brand.ts
//
// The tenant's brand as the drawing layer receives it, and the ONE emitted
// rule that applies it (plan 2026-09-13-a-block-report-wears-the-tenants-brand,
// Besluit 8; ADR-0264).
//
// A brand is a TOKEN OVERRIDE, not a second styling system: every component
// already reads paper, ink, fonts and accent through `var(--verity-…, fallback)`
// (tokens.css), so applying a brand means setting those custom properties on
// one scope element — `[data-verity-brand]` — and nothing else. The literals
// (`background-color`, `color`, `font-family`) ride along for the e-mail: juice
// inlines class/attribute rules as literal declarations, and mail clients drop
// `var()`.
//
// Emitted, never static — the same reason as `pageStyleRule`: the built email
// stylesheet must not carry a rule that applies to every mail ever sent. Three
// surfaces put this string in a `<style>` beside the pages: the reading room
// and the email/PDF entry (`apps/artifact-sandbox/src/runtime/sections.ts`),
// and the report editor's canvas (`ReportCanvas.tsx`).
//
// Per field, never all-or-nothing: an admin who picked only a heading font
// keeps Verity's paper, grain and wash. `--verity-report-wash: none` and the
// ink literal follow ONLY the paper.
//
// The type is declared here, structurally — not imported from
// `@verity/shared-types` — for the same reason `PageFormatSpec` is: the type
// says exactly which fields the drawing layer reads. (The package does depend
// on shared-types, for the composition layout and the edition DTOs.)
// `VerityInitMessage.brand` in shared-types carries the same keys; a drift
// test pins both to the Python producer (`api/schemas/report_brand.py`).

/** Literal CSS values, one per field the admin set. Mirrors the backend's
 * `ResolvedBrand` (`apps/backend/api/schemas/report_brand.py`). */
export type ReportBrandTokens = {
  readonly paper?: string | null;
  readonly ink?: string | null;
  readonly accent?: string | null;
  readonly accent_soft?: string | null;
  readonly font_heading_stack?: string | null;
  readonly font_body_stack?: string | null;
  readonly logo_data_url?: string | null;
  readonly logo_url?: string | null;
};

/** The attribute a surface puts on the element the brand applies to. */
export const BRAND_SCOPE_ATTR = "data-verity-brand";
export const BRAND_SCOPE_SELECTOR = `[${BRAND_SCOPE_ATTR}]`;

// The SINK half of the defence in depth (the source half is the Pydantic
// schema). A value that does not match is SKIPPED, never interpolated: a `}`
// in a value would leave the CSS scope, and this string is text content of a
// <style> (React sets textContent, so HTML cannot break out — CSS could).
const HEX = /^#[0-9a-f]{6}$/;
const FONT_STACK = /^[A-Za-z0-9 ,'"-]+$/;
const DATA_PNG = /^data:image\/png;base64,[A-Za-z0-9+/=]+$/;
const PUBLIC_LOGO = /^https:\/\/[a-z0-9.-]+(?::[0-9]+)?\/api\/brand-assets\/[0-9a-f]{64}\.png$/;

export function isHexColour(v: unknown): v is string {
  return typeof v === "string" && HEX.test(v);
}
export function isFontStack(v: unknown): v is string {
  return typeof v === "string" && FONT_STACK.test(v);
}
export function isLogoSrc(v: unknown): v is string {
  return typeof v === "string" && (DATA_PNG.test(v) || PUBLIC_LOGO.test(v));
}

/** The brand with every invalid field dropped — what the surfaces should
 * hold, so a component reading `brand.accent` never sees a bad value. */
export function sanitizeBrand(brand: ReportBrandTokens | null | undefined): ReportBrandTokens | null {
  if (!brand) return null;
  const out: { -readonly [K in keyof ReportBrandTokens]: ReportBrandTokens[K] } = {};
  if (isHexColour(brand.paper)) out.paper = brand.paper;
  if (isHexColour(brand.ink)) out.ink = brand.ink;
  if (isHexColour(brand.accent)) out.accent = brand.accent;
  if (isHexColour(brand.accent_soft)) out.accent_soft = brand.accent_soft;
  if (isFontStack(brand.font_heading_stack)) out.font_heading_stack = brand.font_heading_stack;
  if (isFontStack(brand.font_body_stack)) out.font_body_stack = brand.font_body_stack;
  if (isLogoSrc(brand.logo_data_url)) out.logo_data_url = brand.logo_data_url;
  if (isLogoSrc(brand.logo_url)) out.logo_url = brand.logo_url;
  return Object.keys(out).length ? out : null;
}

/** The emitted stylesheet for a brand: the empty string when nothing is set,
 * so a brand-less document is byte-identical to one drawn before brands
 * existed. */
export function brandStyleRule(brand: ReportBrandTokens | null | undefined): string {
  const b = sanitizeBrand(brand);
  if (!b) return "";
  const scope: string[] = [];
  const sheet: string[] = [];
  if (b.paper) {
    scope.push(`--verity-report-bg: ${b.paper}`, "--verity-report-wash: none", `background-color: ${b.paper}`);
    sheet.push(`background-color: ${b.paper}`);
  }
  if (b.ink) {
    scope.push(`--verity-color-text: ${b.ink}`, `--verity-color-ink: ${b.ink}`, `color: ${b.ink}`);
  }
  if (b.accent) {
    // Headings, KPI figures and other UI only. The series colours come from
    // the report's chart palette (ChartPaletteContext), which wins over the
    // accent (plan 2026-09-26, Decision 1) — so no chart token here, or the
    // accent would keep reaching the DOM-drawn charts through this scope.
    scope.push(`--verity-color-primary: ${b.accent}`);
  }
  if (b.accent_soft) {
    scope.push(`--verity-color-accent-soft: ${b.accent_soft}`);
  }
  if (b.font_heading_stack) {
    scope.push(`--verity-font-display: ${b.font_heading_stack}`);
  }
  if (b.font_body_stack) {
    scope.push(`--verity-font-family: ${b.font_body_stack}`, `font-family: ${b.font_body_stack}`);
  }
  const lines = [`${BRAND_SCOPE_SELECTOR} { ${scope.join("; ")}; }`];
  if (sheet.length) {
    lines.push(`${BRAND_SCOPE_SELECTOR} .verity-report-page-sheet { ${sheet.join("; ")}; }`);
  }
  return lines.join("\n");
}
